import { Component, OnInit, OnDestroy, Output, EventEmitter } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { Subject, Subscription } from "rxjs";
import { School } from "src/app/shared/models/school";
import { Student } from "src/app/shared/models/student-base";
import { ApiDataService } from "src/app/shared/services/api.service";
import { BtdCalendarLink, BtdCalendarService } from "src/app/shared/services/btd-calendar.service";
import { Location } from "@angular/common";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { takeUntil } from "rxjs/operators";
import { AppRouteConstants } from "src/app/shared/constants/app-route.constants";
import { ErrorMessages } from "src/app/shared/constants/utility.constants";
import { Tutor } from "src/app/shared/models/tutor-base";
import { StorageService } from "src/app/shared/services/storage.service";
import { Lesson } from "../../../lessons/models/lesson";
import { StudentSession } from "src/app/shared/models/student-session";
import { enagagementTabs, errorMessages } from "../../constants/passage-constants";

@Component({
    selector: "pre-engagement",
    templateUrl: "./pre-engagement.component.html",
    styleUrls: ["./pre-engagement.component.css"],
    standalone: false
})

export class PreEngagement implements OnInit, OnDestroy {
  subscription: Subscription;
  loading=true;
  isSavingLoader=false;
  studentControl: UntypedFormControl = new UntypedFormControl();
  schoolControl: UntypedFormControl = new UntypedFormControl();
  schoolList: School[] = [];
  schoolOptions: School[] = [];
  studentList: Student[] = [];
  filteredStudentList: Student[] = [];
  tutorOptions: Tutor[] = [];
  lessonOptions: Lesson[] = [];
  updateStudentProgressGroup: UntypedFormGroup;
  student: any;
  tutorTipModal=false;
  tutorTip = "";
  noBtdFormModal = false;
  private btdCalendarLinks: { [key: string]: BtdCalendarLink } = {};
  sessionData: StudentSession ;
  lessonName: string =''

  private ngUnsubscribe = new Subject <void>();
  constructor(
    private apiService: ApiDataService,
    private toastrService: ToastrService,
    public _location: Location,
    private fb: UntypedFormBuilder,
    private btdCalendarService: BtdCalendarService
  ) {
    this.initializeForm();
  }

  ngOnInit() {
    this.sessionData= StorageService.getSessionData();
    this.getTutorTips()
    this.getSessionDetails()
    this.loadBtdCalendarLinks()
  }

  private loadBtdCalendarLinks() {
    this.btdCalendarService.loadLinks()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((map) => {
        this.btdCalendarLinks = map;
      });
  }

  private getCurrentWeekBtdUrl(): string | null {
    return this.btdCalendarService.getCurrentWeekUrl(this.btdCalendarLinks);
  }

  getTutorTips() { 
    try {
      this.apiService
      .getLessonTutorTips(this.sessionData["lesson"])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          this.tutorTip = res.tutor_tips;
          this.lessonName = res?.lesson_plan_name
        }
      });
    } catch (error) {
      console.error("Error fetching tutor tip:", error);
    }
  }

  getSessionDetails() { 
    try {
      const lessonStudentIds = { lessonId: this.sessionData["lesson"], studentId: this.sessionData["student"] };
      this.apiService
      .getSessionDetails(lessonStudentIds)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          const moodDatas = res?.data?.[0]
          this.setMoodValue(moodDatas?.student_mood_first, "mood_first"); // Same data is for every passage Attempts
          this.setMoodValue(moodDatas?.be_the_diff_complete, "be_the_diff_complete"); // Same data is for every passage Attempts
        }
        this.loading = false;
      });
    } catch (error) {
      this.loading = false;
      console.error("Error fetching tutor tip:", error);
    }
  }

  onBackClick() {
    this.goToTab(enagagementTabs.startPage)
    this.updateStudentProgressGroup.reset();
  }

  tutorTipModalOpen(value:boolean){
    this.tutorTipModal=value
  }
  
  @Output() changeTab = new EventEmitter<number>();
  
  goToTab(index: number) {
    this.changeTab.emit(index);
  }

  onNextClick(studentProgress) {
    if(studentProgress.be_the_diff_complete ==2){
      this.toastrService.error(ErrorMessages.BE_THE_DIFFERENCE_INCOMPLETE);
      return;
    }
    if (studentProgress.mood_first && studentProgress.be_the_diff_complete ) {
      this.updateProgress(studentProgress);
    } else {
      this.toastrService.error(ErrorMessages.PAGE_INCOMPLETE);
    }
  }

  onBeTheDifferenceClick(){
    const url = this.getCurrentWeekBtdUrl();
    if (!url) {
      this.noBtdFormModalOpen(true);
      return;
    }
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      newWindow.opener = null;
    }
  }

  noBtdFormModalOpen(value: boolean) {
    this.noBtdFormModal = value;
  }

  setMoodValue(moodValue: number, controlName: string) {
    if (this.isSavingLoader) return;
    this.updateStudentProgressGroup.get(controlName).setValue(moodValue);
  }

  initializeForm() {
    this.updateStudentProgressGroup = new UntypedFormGroup({
      mood_first: new UntypedFormControl("", Validators.required),
      control_group: new UntypedFormControl(false),
      be_the_diff_complete: new UntypedFormControl("", Validators.required),
    });
  }

  createDefaultPassageGroups() {
    const defaultPassageGroups = [];
    for (let i = 0; i < 2; i++) {
      defaultPassageGroups.push(this.createDisabledPassageFormGroup());
    }
    return defaultPassageGroups;
  }

  createDisabledPassageFormGroup() {
    return this.fb.group({
      second_read_complete: new UntypedFormControl({ value: "", disabled: true }, [
        Validators.required,
      ]),
      passage_id: new UntypedFormControl({ value: '', disabled: true }),
      passage_name: new UntypedFormControl({ value: '', disabled: true }),
      word_count: new UntypedFormControl({ value: '', disabled: true }),
    });
  }

  updateProgress(progress: any) {
    // Current page means the next page to be loaded next time if the session is not fully completed!
    progress.current_page = enagagementTabs.passageEngagement
    progress.lesson = this.sessionData["lesson"];
    progress.volunteer = this.sessionData["volunteer"];
    progress.student = this.sessionData["student"];
    progress.curriculum_name = this.sessionData["curriculum_name"] || null;
    this.getUpdateNewProgress(progress);
  }

  getUpdateNewProgress(progress) {
    this.isSavingLoader = true
      this.apiService.updateSessionEngagement(progress).subscribe(
        (res) => {
          if (res) {
            const nextTabKey = progress.current_page
            this.goToTab(nextTabKey);
            StorageService.setSessionCurrentPage(nextTabKey);
          }
          this.isSavingLoader = false
        },
        (err) => {
          this.isSavingLoader = false
          this.toastrService.error(errorMessages.UPDATE_PROGRESS_FAILED);
        }
      );
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
}
